// src/pages/admin/purchase-order/CreatePurchaseOrder.jsx
import { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card'
import { Button } from '../../../components/ui/Button'
import { Input } from '../../../components/ui/Input'
import CreatePurchaseOrderModal from '../../../components/modals/CreatePurchaseOrderModal'
import { 
  Plus, 
  ShoppingCart, 
  Package, 
  Building, 
  FileText,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  Search,
  Calendar,
  User
} from 'lucide-react'
import PurchaseOrderService from '../../../services/purchaseOrderService'
import InlineLoader from '../../../components/ui/InlineLoader'
import { PurchaseOrderUtils } from '../../../utils/purchaseOrderUtils'

const CreatePurchaseOrder = () => {
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [recentOrders, setRecentOrders] = useState([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({
    totalOrders: 0,
    pendingOrders: 0,
    completedOrders: 0,
    totalAmount: 0
  })
  const [toast, setToast] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')

  // Show toast notification
  const showToast = (message, type = 'success') => {
    setToast({ message, type })
    setTimeout(() => setToast(null), 3000)
  }

  // Fetch recent purchase orders and calculate stats
  const fetchData = async () => {
    try {
      setLoading(true)
      console.log('🛒 Fetching purchase orders data...')
      
      // Fetch all orders and use them for both recent orders and stats
      const response = await PurchaseOrderService.getAllPurchaseOrders()
      
      if (response?.success) {
        const allOrders = response.data?.purchaseOrders || []
        console.log('✅ Fetched', allOrders.length, 'purchase orders')
        
        // Set recent orders (last 5)
        const sortedOrders = [...allOrders].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        setRecentOrders(sortedOrders.slice(0, 5))
        
        // Calculate statistics from orders data
        const calculatedStats = {
          totalOrders: allOrders.length,
          pendingOrders: allOrders.filter(order => order.status === 'pending').length,
          completedOrders: allOrders.filter(order => order.status === 'completed').length,
          totalAmount: allOrders.reduce((total, order) => {
            const orderTotal = PurchaseOrderUtils.calculateOrderTotal(
              order.orderItems?.map(item => ({
                quantity: item.quantity,
                unitPrice: item.productSnapshot?.cost || item.rawMaterialSnapshot?.cost || 0
              })) || []
            )
            return total + orderTotal
          }, 0)
        }
        
        setStats(calculatedStats)
        console.log('📊 Calculated stats:', calculatedStats)
      } else {
        console.error('❌ Failed to fetch purchase orders:', response)
        showToast('Failed to load purchase orders', 'error')
      }
    } catch (error) {
      console.error('❌ Error fetching data:', error)
      showToast('Failed to load data', 'error')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  // Handle order creation success
  const handleOrderCreated = (newOrder) => {
    showToast('Purchase order created successfully!')
    fetchData() // Refresh data
  }

  // Filter recent orders based on search
  const filteredOrders = PurchaseOrderUtils.filterItems(
    recentOrders, 
    searchTerm, 
    ['poNumber', 'supplier.name', 'warehouse.name', 'requestingBranch.name']
  )

  if (loading) {
    return (
      <InlineLoader text="Loading purchase order data..." />
    )
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Create Purchase Order</h1>
        </div>
        <Button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center space-x-2"
        >
          <Plus className="h-5 w-5" />
          <span>New Purchase Order</span>
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Orders</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalOrders}</p>
              </div>
              <div className="bg-blue-100 p-3 rounded-lg">
                <ShoppingCart className="h-6 w-6 text-blue-600" />
              </div>
            </div>
            <div className="mt-2">
              <p className="text-xs text-gray-500">All purchase orders</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Pending Orders</p>
                <p className="text-2xl font-bold text-orange-600">{stats.pendingOrders}</p>
              </div>
              <div className="bg-orange-100 p-3 rounded-lg">
                <AlertCircle className="h-6 w-6 text-orange-600" />
              </div>
            </div>
            <div className="mt-2">
              <p className="text-xs text-gray-500">Awaiting processing</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Completed Orders</p>
                <p className="text-2xl font-bold text-green-600">{stats.completedOrders}</p>
              </div>
              <div className="bg-green-100 p-3 rounded-lg">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
            </div>
            <div className="mt-2">
              <p className="text-xs text-gray-500">Successfully completed</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Value</p>
                <p className="text-2xl font-bold text-purple-600">
                  {PurchaseOrderUtils.formatCurrency(stats.totalAmount)}
                </p>
              </div>
              <div className="bg-purple-100 p-3 rounded-lg">
                <FileText className="h-6 w-6 text-purple-600" />
              </div>
            </div>
            <div className="mt-2">
              <p className="text-xs text-gray-500">Total order value</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Package className="h-5 w-5 text-blue-600" />
            <span>Quick Actions</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center justify-center space-x-2 h-24 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700"
            >
              <Plus className="h-6 w-6" />
              <div className="text-left">
                <div className="font-medium">New Purchase Order</div>
                <div className="text-sm opacity-90">Create a new PO</div>
              </div>
            </Button>

            <Button
              variant="outline"
              onClick={() => window.location.href = '/admin/purchase-order/all'}
              className="flex items-center justify-center space-x-2 h-24 border-2 hover:bg-gray-50"
            >
              <FileText className="h-6 w-6 text-gray-600" />
              <div className="text-left">
                <div className="font-medium text-gray-900">View All Orders</div>
                <div className="text-sm text-gray-600">Browse order history</div>
              </div>
            </Button>

            <Button
              variant="outline"
              onClick={fetchData}
              className="flex items-center justify-center space-x-2 h-24 border-2 hover:bg-gray-50"
            >
              <RefreshCw className="h-6 w-6 text-gray-600" />
              <div className="text-left">
                <div className="font-medium text-gray-900">Refresh Data</div>
                <div className="text-sm text-gray-600">Update statistics</div>
              </div>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Recent Orders */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center space-x-2">
              <FileText className="h-5 w-5 text-green-600" />
              <span>Recent Purchase Orders</span>
            </CardTitle>
            <div className="flex items-center space-x-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  type="text"
                  placeholder="Search orders..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-64"
                />
              </div>
              <Button
                variant="outline"
                onClick={() => window.location.href = '/admin/purchase-order/all'}
                className="text-sm"
              >
                View All
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {filteredOrders.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      PO Number
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Supplier/Warehouse
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Branch
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Order Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Items
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredOrders.map((order) => {
                    const statusConfig = PurchaseOrderUtils.getStatusConfig(order.status)
                    
                    return (
                      <tr key={order._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <ShoppingCart className="h-4 w-4 text-gray-400 mr-2" />
                            <span className="text-sm font-medium text-gray-900 font-mono">
                              {order.poNumber || 'N/A'}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <Building className="h-4 w-4 text-gray-400 mr-2" />
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {order.supplier?.name || order.warehouse?.name || 'N/A'}
                              </div>
                              <div className="text-sm text-gray-500 capitalize">
                                {order.supplierType}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {order.requestingBranch?.name || 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                            <span className="text-sm text-gray-900">
                              {PurchaseOrderUtils.formatDate(order.orderDate)}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusConfig.bgColor} ${statusConfig.textColor}`}>
                            {/* <span className="mr-1">{statusConfig.icon}</span> */}
                            {statusConfig.label}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <Package className="h-4 w-4 text-gray-400 mr-2" />
                            <span className="text-sm text-gray-900">
                              {order.orderItems?.length || 0} items
                            </span>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12">
              <ShoppingCart className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 text-lg font-medium">No purchase orders found</p>
              <p className="text-gray-400 mt-1">
                {searchTerm ? 'Try adjusting your search terms' : 'Create your first purchase order to get started'}
              </p>
              {!searchTerm && (
                <Button
                  onClick={() => setShowCreateModal(true)}
                  className="mt-4"
                >
                  Create Purchase Order
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Purchase Order Modal */}
      <CreatePurchaseOrderModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onOrderCreated={handleOrderCreated}
        mode="create"
      />

      {/* Toast Notification */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg ${
          toast.type === 'success' 
            ? 'bg-green-100 border border-green-200 text-green-800' 
            : 'bg-red-100 border border-red-200 text-red-800'
        }`}>
          <div className="flex items-center space-x-2">
            {toast.type === 'success' ? (
              <CheckCircle className="h-5 w-5" />
            ) : (
              <AlertCircle className="h-5 w-5" />
            )}
            <span className="font-medium">{toast.message}</span>
          </div>
        </div>
      )}
    </div>
  )
}

export default CreatePurchaseOrder